import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../assets/icons/popup/e-lotto-pcso-logo 1.svg?react';
import BgLatto from '../../assets/images/bg-latto.jpg';
import BgPattern from '../../assets/images/bg-pattern.png';
import { Download, Printer, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import { gamesApi } from '../../api/games';
import { transactionsApi } from '../../api/transactions';
import { userApi } from '../../api/user';
import getGameImage from '../../utils/getGameImage';
import getGameType from '../../utils/games';
import * as htmlToImage from 'html-to-image';
import toast, { Toaster } from 'react-hot-toast';
import TicketTemplate1 from '../../ticket-templates/template-1';
import TicketTemplate2 from '../../ticket-templates/template-2';
import TicketTemplate3 from '../../ticket-templates/template-3';
import TicketTemplate4 from '../../ticket-templates/template-4';
import TicketTemplate5 from '../../ticket-templates/template-5';
import TicketTemplate6 from '../../ticket-templates/template-6';
import { useMemo } from 'react';

const ticketTemplates = {
  1: TicketTemplate1,
  2: TicketTemplate2,
  3: TicketTemplate3,
  4: TicketTemplate4,
  5: TicketTemplate5,
  6: TicketTemplate6,
};

export default function TransactionTicketPopup({
  isOpen,
  onClose,
  ticketId,
  onLoading = () => {},
}) {
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(false);
  const [games, setGames] = useState([]);
  const [transaction, setTransaction] = useState(null);
  const { tokens, user } = useAuth();
  const [hasPrinter, setHasPrinter] = useState(false);
  const [userTicketTemplate, setUserTicketTemplate] = useState(1);
  const [ticketDisplayName, setTicketDisplayName] = useState('');
  const [userTicketMeta, setUserTicketMeta] = useState({});
  const [groupedBets, setGroupedBets] = useState({});

  const TicketTemplate = useMemo(() => {
    return ticketTemplates[userTicketTemplate];
  }, [userTicketTemplate]);

  useEffect(() => {
    if (window.ReactNativeWebView) {
      setHasPrinter(true);
    }
    const fetchTicketDisplayName = async () => {
      const response = await userApi.getUserById(tokens.access.token, user.id);
      setUserTicketTemplate(
        response.data.user.UserTicketTemplate?.templateId || 1
      );
      const threeDigitsFromMobileNumber =
        response.data.user.mobileNumber.slice(-3);
      if (response.data.user.ticketDisplayName) {
        setTicketDisplayName(response.data.user.ticketDisplayName);
      } else {
        setTicketDisplayName(threeDigitsFromMobileNumber);
      }
      setUserTicketMeta(response.data.user.ticketMeta || {});
    };
    fetchTicketDisplayName();
  }, [tokens.access.token, user.id]);

  useEffect(() => {
    const fetchTransaction = async () => {
      if (!isOpen || !ticketId) return;

      try {
        onLoading(true);
        const response = await transactionsApi.getTransactionById(
          tokens.access.token,
          ticketId
        );
        if (response.status === 200) {
          setTransaction(response.data);
        }
      } finally {
        onLoading(false);
      }
    };

    if (isOpen) setTransaction(null);
    fetchTransaction();
  }, [isOpen, ticketId]);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        // Fetch all games to get the image mapping
        const allGames = await gamesApi.getAllGames(tokens.access.token);
        const gameSettings = allGames.find(
          (game) => game.id === transaction?.gameSettingsId
        );
        const image = gameSettings?.otherSettings?.image;
        const gameType = getGameType(image);
        setGames({ gameType });
      } catch (error) {
        console.error('Error fetching games:', error);
      }
    };

    if (transaction?.gameSettingsId) {
      fetchGames();
    }
  }, [tokens.access.token, transaction]);

  useEffect(() => {
    if (transaction?.bets) {
      setGroupedBets(groupBetsByGameSetting(transaction.bets));
    }
  }, [transaction]);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, transaction]);

  if (!isVisible || !transaction) return null;

  const formatPeso = () => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(transaction.totalAmount);
  };

  const formatDrawTime = (timeStr) => {
    if (!timeStr) return 'Loading...';
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours));
    date.setMinutes(parseInt(minutes));
    // If minutes are 00, show as '5PM', else '5:30 PM'
    if (minutes === '00' || minutes === undefined) {
      return date
        .toLocaleTimeString('en-US', {
          hour: 'numeric',
          hour12: true,
        })
        .replace(' ', '')
        .replace(':00', '');
    }
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const currentDate = new Date(transaction.drawDate).toLocaleDateString(
    'en-PH',
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Manila',
    }
  );
  const currentTime = new Date(transaction.drawDate).toLocaleTimeString(
    'en-PH',
    {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Manila',
    }
  );

  // Helper to check if all drawTimes are the same (compare only HH:mm)
  const getDrawTimeDisplay = () => {
    if (!transaction?.bets || transaction.bets.length === 0) return '';
    const allDrawTimes = transaction.bets.map((bet) =>
      bet.drawTime ? bet.drawTime.slice(0, 5) : ''
    );
    const firstDrawTime = allDrawTimes[0];
    const allSame = allDrawTimes.every((dt) => dt === firstDrawTime);
    return allSame ? formatDrawTime(firstDrawTime) : 'Multiple Draws';
  };

  const drawTime = getDrawTimeDisplay();

  const formatValue = (value) => {
    return value.toFixed(2).replace(/\.00$/, '');
  };

  const toCurrency = (value) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  function groupBetsByGameSetting(bets) {
    const grouped = {};
    bets.forEach((bet) => {
      const key = bet.gameSettingsId;
      if (!grouped[key]) {
        grouped[key] = {
          gameName: bet.GameSetting?.name || `Game ${key}`,
          bets: [],
          gameType: bet.GameSetting?.otherSettings?.image || '',
        };
      }
      grouped[key].bets.push(bet);
    });
    return grouped;
  }

  const getBetType = (bet) => {
    return bet.bettingOption === 'ramble'
      ? 'R'
      : bet.bettingOption === 'S/O'
      ? 'S/O'
      : 'S';
  };

  const text = (selectedGameId = null) => {
    if (selectedGameId && groupedBets[selectedGameId]) {
      const selectedGame = groupedBets[selectedGameId];
      const selectedTotal = selectedGame.bets.reduce(
        (sum, bet) => sum + Number(bet.amount || 0),
        0
      );

      let selectedText = `Game: ${selectedGame.gameName}\n`;
      selectedText += `Ticket: ${transaction.id}\n`;
      selectedText += `Date: ${currentDate}\n`;
      selectedText += `Draw: ${drawTime}\n`;
      selectedText += 'Combinations:\n';

      selectedGame.bets.forEach((bet) => {
        selectedText += `${bet.combination} (${getBetType(bet)}) = ${toCurrency(
          bet.amount
        )}\n`;
      });

      selectedText += `\nTotal Amount: ${toCurrency(selectedTotal)}`;
      return selectedText;
    }

    let text = '';
    if (Object.keys(groupedBets).length > 1) {
      text += `Ticket: ${transaction.id}\n`;
      text += `Date: ${currentDate}\n`;
      text += `Draw: ${drawTime}\n`;
      text += 'Combinations:\n';
      Object.entries(groupedBets).forEach(([, group]) => {
        text += `\n${group.gameName}:\n`;
        group.bets.forEach((bet) => {
          text += `${bet.combination} (${getBetType(bet)}) = ${toCurrency(
            bet.amount
          )}\n`;
        });
      });
    } else {
      const firstGroup = Object.values(groupedBets)[0];
      text += `Game: ${firstGroup?.gameName || ''}\n`;
      text += `Ticket: ${transaction.id}\n`;
      text += `Date: ${currentDate}\n`;
      text += `Draw: ${drawTime}\n`;
      text += 'Combinations:\n';
      firstGroup?.bets.forEach((bet) => {
        text += `${bet.combination} (${getBetType(bet)}) = ${toCurrency(
          bet.amount
        )}\n`;
      });
    }
    text += `\nTotal Amount: ${toCurrency(transaction.totalAmount)}`;
    return text;
  };

  const copyText = (selectedGameId = null) => {
    navigator.clipboard.writeText(text(selectedGameId));

    if (selectedGameId && groupedBets[selectedGameId]) {
      toast.success(`${groupedBets[selectedGameId].gameName} text copied`);
      return;
    }

    toast.success('Text copied to clipboard');
  };

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex justify-center p-4 z-50 font-poppins transition-opacity duration-300 overflow-auto ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className='fixed inset-0' onClick={onClose}></div>
      <div
        className={`relative w-full max-w-md rounded-3xl overflow-auto transform transition-all duration-300 ease-in-out grid place-items-center ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{
          backgroundImage: `url('${BgLatto}'), linear-gradient(132.14deg, #0279F9 0%, #293CA8 40%, #D42F55 100%)`,
          backgroundBlendMode: 'soft-light', // Ensures the gradient and image blend nicely
          backgroundSize: 'cover', // Ensures the image covers the card
          backgroundPosition: 'center', // Centers the image
        }}
      >
        {/* Content */}
        <div className='relative text-center'>
          {/* Close button */}
          <button
            onClick={onClose}
            className='absolute z-50 right-4 top-4 text-white/80 hover:text-white transition-colors'
            aria-label='Close'
          >
            <svg
              className='w-6 h-6'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
          {/* Logo */}
          <div className='flex justify-center text-white'>
            <h3 className='text-3xl font-semibold my-5 max-xs:my-3'>
              Bet Ticket
            </h3>
          </div>
          {/* Ticket section start */}
          <div
            id='ticket-section'
            className='bg-white rounded-3xl px-6 py-4  max-xs:py-3 relative overflow-hidden font-mono max-w-sm'
          >
            {/* Background Pattern */}
            <div
              className='absolute inset-0 opacity-[0.9]'
              style={{
                backgroundImage: `url('${BgPattern}')`,
                backgroundBlendMode: 'overlay', // Ensures the gradient and image blend nicely
                backgroundSize: 'cover', // Ensures the image covers the card
                backgroundPosition: 'center', // Centers the image
              }}
            />

            {/* Content */}
            <div className='relative'>
              {/* Header */}
              <div className='flex items-center justify-center mb-4'>
                <Logo className='rounded-lg w-[144px] h-[37.7px] ' />
              </div>

              {/* Game Info */}
              <div className='flex items-center justify-between gap-3 mb-4'>
                <div className='flex items-center gap-2'>
                  {games.gameType && (
                    <img
                      src={getGameImage(games.gameType)}
                      className='w-9 h-9 max-xs:w-10 max-xs:h-10 max-xs:text-xs'
                    />
                  )}
                  <span className='font-normal text-base'>
                    {transaction?.gameName}
                  </span>
                </div>

                <div className='text-sm font-normal text-right max-xs:text-xs'>
                  {currentDate} <br />
                  {currentTime}
                </div>
              </div>

              {/* Ticket Number */}
              <div className='border border-black rounded-md p-2 font-normal '>
                <div className='text-sm  text-black mb-1'>Ticket #:</div>
                <div className=' text-sm max-xs:text-xs'>{transaction.id}</div>
              </div>

              {/* Date and Draw Time */}
              <div className='my-5 text-left max-xs:my-3 '>
                <div className='text-sm max-xs:text-xs'>
                  Date: {currentDate}
                </div>
                <div className='text-sm max-xs:text-xs'>Draw: {drawTime}</div>
              </div>

              <div className='flex justify-between items-center mb-3'>
                {/* Combinations */}
                <div className='w-full'>
                  <div className='text-sm mb-0.5 text-left max-xs:text-xs'>
                    Combinations
                  </div>
                  {/* Group all bets into a single table as per new design */}
                  {(() => {
                    if (!transaction?.bets) return null;
                    // Sort bets by drawTime, then by game
                    const sortedBets = [...transaction.bets].sort((a, b) => {
                      if (a.drawTime !== b.drawTime)
                        return a.drawTime.localeCompare(b.drawTime);
                      const gameA = a.GameSetting?.name || a.gameName || '';
                      const gameB = b.GameSetting?.name || b.gameName || '';
                      return gameA.localeCompare(gameB);
                    });
                    return (
                      <div className='w-full overflow-x-auto'>
                        <table className='w-full text-xs mb-2 border border-black mt-2'>
                          <thead>
                            <tr>
                              <th className='py-1 px-2 border border-black'>
                                Combi
                              </th>
                              <th className='py-1 px-2 border border-black'>
                                Game
                              </th>
                              <th className='py-1 px-2 border border-black'>
                                Type
                              </th>
                              <th className='py-1 px-2 border border-black'>
                                Amount
                              </th>
                              <th className='py-1 px-2 border border-black'>
                                Winnings
                              </th>
                              <th className='py-1 px-2 border border-black'>
                                Draw
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedBets.map((bet, index) => (
                              <tr key={index} className='text-center'>
                                <td className='whitespace-nowrap py-1 px-2 border border-black'>
                                  {bet.combination}
                                </td>
                                <td className='whitespace-nowrap py-1 px-2 border border-black'>
                                  {bet.GameSetting?.name || bet.gameName || ''}
                                </td>
                                <td className='whitespace-nowrap py-1 px-2 border border-black'>
                                  {bet.bettingOption === 'ramble'
                                    ? 'R'
                                    : bet.bettingOption === 'S/O'
                                    ? 'S/O'
                                    : 'S'}
                                </td>
                                <td className='whitespace-nowrap py-1 px-2 border border-black'>
                                  {bet.amount}
                                </td>
                                <td className='whitespace-nowrap py-1 px-2 border border-black'>
                                  {bet.bettingOption === 'S/O'
                                    ? 'S/O'
                                    : Number(bet.winAmount).toLocaleString(
                                        'en-US',
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        }
                                      )}
                                </td>
                                <td className='whitespace-nowrap py-1 px-2 border border-black'>
                                  {formatDrawTime(bet.drawTime)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Total Amount */}
              <div className='text-lg max-xs:text-base font-semibold mb-2.5'>
                Total Amount Paid: {formatPeso()}
              </div>

              {/* ticket display name from the user*/}
              <div className='text-sm max-xs:text-xs text-right'>
                {ticketDisplayName}
              </div>

              {/* QR Code */}
              <div className='w-[110px] mx-auto'>
                <QRCodeSVG value={transaction.id} size={110} />
              </div>

              {/* Total Amount */}
              <div className='text-lg font-semibold max-xs:text-base'>
                <Barcode value={transaction.id} className='rounded-lg w-full' />
              </div>
            </div>
          </div>
          {/* Ticket section end */}
          {/* Text */}
          <button
            className=' bg-[#0E64D1] w-2/3 hover:bg-blue-600 text-white p-2.5 max-xs:py-2 rounded-md transition-colors flex items-center justify-center gap-2.5 mx-auto my-2.5 max-xs:my-1 max-xs:text-sm '
            onClick={() => {
              const ticketSection = document.getElementById('ticket-section');
              if (ticketSection) {
                htmlToImage.toPng(ticketSection).then((dataUrl) => {
                  const link = document.createElement('a');
                  link.href = dataUrl;
                  link.download = `ticket-${transaction.id}.png`;
                  link.click();
                });
              }
            }}
          >
            Download Ticket
            <Download />
          </button>
          {hasPrinter && (
            <button
              className=' bg-[#0E64D1] w-2/3 hover:bg-blue-600 text-white p-2.5 max-xs:py-2 rounded-md transition-colors flex items-center justify-center gap-2.5 mx-auto my-2.5 max-xs:my-1 max-xs:text-sm '
              onClick={() => {
                const tickets = document.querySelectorAll('.tickets');
                if (tickets.length > 1) {
                  tickets.forEach(async (ticket) => {
                    const dataUrl = await htmlToImage.toJpeg(ticket, {
                      quality: 0.95,
                    });
                    if (window.ReactNativeWebView) {
                      window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                          data_img: dataUrl,
                          printerWidth: userTicketTemplate === 2 ? 550 : null,
                        })
                      );
                    }
                  });
                  return;
                }

                const ticketSection = document.getElementById('print-ticket');
                if (ticketSection) {
                  htmlToImage
                    .toJpeg(ticketSection, { quality: 0.95 })
                    .then((dataUrl) => {
                      if (window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(
                          JSON.stringify({
                            data_img: dataUrl,
                            printerWidth: userTicketTemplate === 2 ? 550 : null,
                          })
                        );
                      }
                    });
                }
              }}
            >
              Print Ticket
              <Printer />
            </button>
          )}
          {/* Buttons */}
          <div className='flex justify-between gap-2 w-2/3 mx-auto text-base max-xs:text-sm mb-3 max-xs:mb-2 '>
            <button
              className='flex-1 bg-[#0E64D1]   text-white p-2.5 max-xs:p-2 rounded-md transition-colors'
              href={`sms:?body=${encodeURI(text())}`}
            >
              Send as Text
            </button>
            <button
              className='flex-1  bg-[#0E64D1] border border-blue-600  text-white  p-2.5  max-xs:p-2 rounded-md transition-colors'
              onClick={copyText}
            >
              Copy Text
            </button>
          </div>

          {Object.keys(groupedBets).length > 1 && (
            <div className='w-2/3 mx-auto mb-7 max-xs:mb-3'>
              <div className='flex items-center gap-2 mb-2'>
                <div className='flex-1 h-px bg-white/20' />
                <span className='text-white/50 text-[10px] uppercase tracking-widest whitespace-nowrap'>
                  Copy by game
                </span>
                <div className='flex-1 h-px bg-white/20' />
              </div>
              <div className='grid grid-cols-2 gap-2'>
                {Object.entries(groupedBets).map(([gameId, gameData]) => (
                  <button
                    key={gameId}
                    className='flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/30 text-white text-xs font-semibold py-2 px-2 rounded-lg transition-all duration-200 uppercase tracking-wide'
                    onClick={() => copyText(gameId)}
                  >
                    <Copy size={12} />
                    {gameData.gameName}
                  </button>
                ))}
              </div>
            </div>
          )}
          <TicketTemplate
            transaction={transaction}
            displayName={ticketDisplayName}
            userName={user.name}
            userTicketMeta={userTicketMeta || {}}
          />
        </div>
      </div>
    </div>
  );
}
